import { denormalize, schema } from "normalizr";
import ApiService from "../../services/api.service";
import { Middleware } from "redux";
import { RootState } from "../reducers";
import { toast } from "react-toastify";

type ReduxAction = {
  type: string;
  payload?: any;
};

// Persist the board to the database after almost every action.
const persistMiddleware: Middleware<{}, RootState> = store => next => (action: ReduxAction) => {
  next(action);
  const { user, boardsById, listsById, cardsById, currentBoardId: boardId, boardType } = store.getState();
  const api = ApiService.getInstance();
  const ignoredActions = [
    "SET_USER",
    "SET_THEME",
    "PUT_BOARD_ID_IN_REDUX",
    "SET_BOARD",
    "SET_BOARDS",
    "SET_LISTS",
    "SET_CARDS",
    "BOARD_USERS",
    "BOARD_TYPE",
    // Board creation is handled explicitly in Home.tsx handleCreateBoard (awaited POST)
    "ADD_BOARD",
    // Card field update actions - these update Redux state but don't trigger individual persistence
    // The SAVE_CARD action (dispatched when modal closes) triggers persistence with all changes
    "CHANGE_CARD_TEXT",
    "CHANGE_CARD_DESCRIPTION",
    "CHANGE_CARD_COMMENT",
    "CHANGE_CARD_ISSUE_TYPE",
    "CHANGE_CARD_ESTIMATE",
    "CHANGE_CARD_CATEGORY",
    "CHANGE_CARD_SUBCATEGORY",
    "CHANGE_CARD_OWNER",
    "CHANGE_CARD_ETA",
    // CHANGE_CARD_STATUS is ignored here because drag-drop dispatches it right before MOVE_CARD.
    // The MOVE_CARD PUT already includes the updated status (Redux state is updated first).
    // Persisting both would cause a race condition (two near-simultaneous PUTs).
    // Modal status changes are persisted via SAVE_CARD when the modal closes.
    "CHANGE_CARD_STATUS",
    "SET_CARD_SUBTASKS",
    "ADD_SUBTASK",
    "UPDATE_SUBTASK",
    "DELETE_SUBTASK",
    "TOGGLE_SUBTASK",
    "CHANGE_CARD_PRIORITY",
    "CHANGE_CARD_LABELS",
    // UI-only actions
    "SET_SEARCH_TERM",
    "CLEAR_SEARCH_TERM"
  ];

  // Nothing is persisted for guest users
  if (user) {
    if (action.type === "DELETE_BOARD") {
      api.delete(`/boards/${boardId}`).catch(err => {
        console.error("Failed to delete board:", err);
        toast.error("Failed to delete board. Please try again.");
      });

      // All action-types that are not DELETE_BOARD or PUT_BOARD_ID_IN_REDUX are currently modifying a board in a way that should
      // be persisted to db. If other types of actions are added, this logic will get unwieldy.
    } else if (!ignoredActions.some(item => item === action.type)) {
      if (boardType === "JIRA") {
        if (action.type === "MOVE_CARD") {
          let moveCard = {
            id: boardId,
            lists: [
              {
                id: action.payload.destListId,
                title: action.payload.destinationListTitle,
                cards: [
                  {
                    id: action.payload.cardId
                  }
                ]
              }
            ]
          };

          api.post(`/boards/changestatus?boardType=` + boardType, moveCard).catch(err => {
            console.error("Failed to move card (JIRA):", err);
            toast.error("Failed to move task. Please refresh and try again.");
          });
        } else {
          let updatedBoardCard = {
            id: boardId,
            lists: [
              {
                id: action.payload.listId,
                cards: [
                  {
                    id: action.payload.cardId,
                    text: action.payload.cardText,
                    description: action.payload.cardDescription,
                    comment: action.payload.cardComment,
                    issueType: {
                      id: action.payload.cardIssueTypeId
                    }
                  }
                ]
              }
            ]
          };

          api.put(`/boards/` + boardId + `?boardType=` + boardType, updatedBoardCard).catch(err => {
            console.error("Failed to update card (JIRA):", err);
            toast.error("Failed to save changes. Please refresh and try again.");
          });
        }
      } else {
        // Guard: skip persistence if no board is loaded
        if (!boardId || !boardsById[boardId]) {
          return;
        }

        // Transform the flattened board state structure into the tree-shaped structure that the db uses.
        const card = new schema.Entity("cardsById", {}, { idAttribute: "_id" });
        const list = new schema.Entity("listsById", { cards: [card] }, { idAttribute: "_id" });
        const board = new schema.Entity("boardsById", { lists: [list] }, { idAttribute: "_id" });
        const entities = { cardsById, listsById, boardsById };

        const boardData = denormalize(boardId, board, entities) as any;

        if (!boardData || !boardData.lists) {
          console.error("Failed to denormalize board data for persistence", { boardId, action: action.type });
          return;
        }

        // Backend expects _id fields (MongoDB format), but also accepts id as alias
        // Keep both _id and id for compatibility
        boardData.id = boardData._id;

        // Always add boardType as AUTOMATION (all boards are automation boards now)
        boardData.boardType = "AUTOMATION";

        for (let i = 0; i < boardData.lists.length; i++) {
          // Keep both _id and id for lists
          boardData.lists[i].id = boardData.lists[i]._id;
          for (let j = 0; j < boardData.lists[i].cards.length; j++) {
            const card = boardData.lists[i].cards[j];
            // Keep both _id and id for cards
            card.id = card._id;

            // Remove empty string fields that might cause backend validation errors
            // The backend may reject empty strings for certain fields
            const fieldsToClean = ['category', 'subcategory', 'owner', 'eta', 'status', 'description', 'estimateTime'];
            // Remove frontend-only fields that backend doesn't recognize
            const frontendOnlyFields = ['statusChangedAt', 'createdAt'];
            fieldsToClean.forEach(field => {
              if (card[field] === '') {
                delete card[field];
              }
            });
            frontendOnlyFields.forEach(field => {
              delete card[field];
            });

            // Handle comment field specially - ensure it's always an array, and remove if empty
            // The backend expects comment as an array of objects, not a string
            if (card.comment) {
              // Ensure comment is an array
              if (!Array.isArray(card.comment)) {
                // If it's not an array, try to convert or remove it
                console.warn('⚠️ Card comment is not an array, removing:', card.comment);
                delete card.comment;
              } else if (card.comment.length === 0) {
                // Remove empty comment arrays to avoid backend errors
                delete card.comment;
              } else {
                // Clean up comment objects - remove any edit flags that shouldn't be persisted
                card.comment = card.comment.map(comment => {
                  const { edit, ...cleanComment } = comment;
                  return cleanComment;
                });
              }
            }

            // Remove empty arrays for other fields
            if (card.subtasks && card.subtasks.length === 0) {
              delete card.subtasks;
            }
          }
        }

        // Clean the payload to ensure no duplicate keys
        // This prevents issues with JSON serialization creating duplicate fields
        const cleanPayload = JSON.parse(JSON.stringify(boardData));

        // All board updates use PUT (board creation is handled explicitly in Home.tsx)
        api.put(`/boards/${boardId}?boardType=AUTOMATION`, cleanPayload).catch(err => {
          console.error("Failed to persist board update:", err, "Action:", action.type);
          toast.error("Failed to save changes. Please refresh and try again.");
        });
      }
    }
  }
};

export default persistMiddleware;
